import { Dispatch, SetStateAction } from 'react';
import { SubscriptionPlan } from '@/modules/models/Variant';
import { bridgeAtom, useValue } from '@/store';
import {
  Label,
  OldPrice,
  Price,
  PurchaseOption,
  PurchaseOptionMarker,
  PurchaseOptionMarkerWrapper,
  SubscriptionOption,
  SubscriptionOptionsLabel,
  SubscriptionOptionsWrapper,
  SubscriptionWrapper,
} from './Subscription.styles';

const getDiscount = (basePrice: number, currentPrice: number) => {
  if (basePrice <= 0 || isNaN(basePrice) || isNaN(currentPrice)) {
    return 0;
  }
  return Math.round(((basePrice - currentPrice) / basePrice) * 100);
};

const getAverageDiscount = (discounts: number[]) => {
  return Math.round(discounts.reduce((acc, discount) => acc + discount, 0) / discounts.length);
};

const getPriceNumber = (price: string) => {
  if (!price) {
    return 0;
  }
  return parseFloat(price.replace(/[^0-9.]/g, ''));
};

const formatPlanName = (planName: string) => {
  const match = planName.match(/(\d+)\s*(months?)/i);
  if (match) {
    return `${match[1]} ${match[2].toLowerCase()}`;
  }

  if (/months?/i.test(planName)) {
    return '1 month';
  }

  return planName;
};

type SubscriptionProps = {
  subscriptionPlans: SubscriptionPlan[];
  oneTimePrice: string;
  setSelectedSubscription: Dispatch<SetStateAction<SubscriptionPlan | null>>;
  selectedSubscription: SubscriptionPlan | null;
  isMinimalistic: boolean;
};

export const Subscription = ({
  subscriptionPlans,
  oneTimePrice,
  setSelectedSubscription,
  selectedSubscription,
  isMinimalistic,
}: SubscriptionProps) => {
  const { translations } = useValue(bridgeAtom);

  const sortedSubscriptionPlans = [...subscriptionPlans].sort((a, b) => {
    const aNum = parseInt(formatPlanName(a.subscription_plan_name)) || 1;
    const bNum = parseInt(formatPlanName(b.subscription_plan_name)) || 1;
    return aNum - bNum;
  });

  const discounts = sortedSubscriptionPlans.map((plan) =>
    getDiscount(getPriceNumber(oneTimePrice), getPriceNumber(plan.subscription_price))
  );
  const averageDiscount = getAverageDiscount(discounts);
  const isSubscribeExpanded = Boolean(selectedSubscription);

  return (
    <SubscriptionWrapper className="product-subscription">
      {!isMinimalistic && (
        <>
          <Label>{window.LLL(translations.subscriptions.mainLabel)}</Label>
          <PurchaseOption>
            <div>
              <PurchaseOptionMarkerWrapper role="button" onClick={() => setSelectedSubscription(null)}>
                <PurchaseOptionMarker selected={!selectedSubscription} />
                <p>{window.LLL(translations.subscriptions.oneTimePurchaseLabel)}</p>
              </PurchaseOptionMarkerWrapper>
              <Price>{oneTimePrice}</Price>
            </div>
          </PurchaseOption>
        </>
      )}
      <PurchaseOption>
        <div>
          <PurchaseOptionMarkerWrapper
            role="button"
            onClick={() => {
              if (selectedSubscription && isMinimalistic) {
                setSelectedSubscription(null);
              } else {
                setSelectedSubscription(sortedSubscriptionPlans[0]);
              }
            }}
          >
            <PurchaseOptionMarker selected={Boolean(selectedSubscription)} />
            <p>{window.LLL(translations.subscriptions?.subscribePurchaseLabel, { averageDiscount })}</p>
          </PurchaseOptionMarkerWrapper>
          <div>
            {isSubscribeExpanded && <OldPrice>{oneTimePrice}</OldPrice>}
            <Price>{selectedSubscription?.subscription_price ?? sortedSubscriptionPlans[0].subscription_price}</Price>
          </div>
        </div>
        {isSubscribeExpanded && (
          <>
            <SubscriptionOptionsLabel>{window.LLL(translations.subscriptions.deliverLabel)}</SubscriptionOptionsLabel>
            <SubscriptionOptionsWrapper>
              {sortedSubscriptionPlans.map((plan) => (
                <SubscriptionOption
                  key={plan.subscription_plan_id}
                  selected={plan.subscription_plan_id === selectedSubscription?.subscription_plan_id}
                  onClick={() => setSelectedSubscription(plan)}
                >
                  <p>{formatPlanName(plan.subscription_plan_name)}</p>
                  <p>save {getDiscount(getPriceNumber(oneTimePrice), getPriceNumber(plan.subscription_price))}%</p>
                </SubscriptionOption>
              ))}
            </SubscriptionOptionsWrapper>
          </>
        )}
      </PurchaseOption>
    </SubscriptionWrapper>
  );
};
